import {
    type CurrentIncomeSplitSnapshot,
    IncomeSplitConfigCreatedVia,
    type MoneyMap,
    MoneyMapAdvisoryMode,
    MoneyMapStatus,
    type MoneyMapWithGroveSplitContext,
} from '@fruitful/types';
import { Link } from 'expo-router';
import { useAtomValue } from 'jotai';
import { Badge } from 'primereact/badge';
import { Column } from 'primereact/column';
import {
    confirmDialog,
    type ConfirmDialogProps,
} from 'primereact/confirmdialog';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
} from 'primereact/datatable';
import React, { useState } from 'react';
import {
    ArchiveIcon,
    BadgeCheckIcon,
    BodyMediumRegular,
    BotIcon,
    Icon,
    IconButton,
    MoneyMapIcon,
    Spinner,
    TooltipTrigger,
    UserCheckIcon,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { TextEditor } from '../../common/components/GridCellEditors';
import {
    DeleteItemRenderer,
    DuplicateItemRenderer,
    EditItemRenderer,
    ViewItemRenderer,
} from '../../common/components/GridCellRenderers';
import { Button } from '../../common/components/GroveButtons';
import { TextError } from '../../common/components/InlineError';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { type BadgeSeverity } from '../../common/types/Badge';
import { formatToSimpleDateTimeString } from '../../common/utils/formatting';
import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../entities/entityAtoms';
import { captureException } from '../../logger/Logger';
import { useCreateMoneyMapperDraft } from '../hooks/useCreateMoneyMapperDraft';
import { useDeleteMoneyMapAndRefetch } from '../hooks/useDeleteMoneyMap';
import { useDuplicateMoneyMap } from '../hooks/useDuplicateMoneyMap';
import { useGetMoneyMapsForMembership } from '../hooks/useGetMoneyMapsForMembership';
import { useArchiveMoneyMap } from '../hooks/useUpdateMoneyMapStatus';
import { useUpdateMoneyMapTitle } from '../hooks/useUpdateMoneyMapTitle';
import {
    getIncomeSplitCreatedViaLabel,
    MoneyMapGuideReadableStatusMap,
} from '../types/MoneyMapGuideReadableStatus';

export const MoneyMapTable = () => {
    const toast = useToastController();

    const adminUser = useAtomValue(adminUserAtom);
    const canMutateMoneyMaps =
        adminUser.authorization.selectedMembership.accounts.canEdit;
    const membership = useAtomValue(selectedMembershipAtom);

    const {
        data: membershipMoneyMaps,
        error: errorLoadingMoneyMaps,
        isLoading: isLoadingMoneyMaps,
        refetch: refetchMoneyMaps,
    } = useGetMoneyMapsForMembership({
        membershipId: membership?.id ?? '',
    });
    const moneyMaps = membershipMoneyMaps?.moneyMaps;
    const currentIncomeSplit = membershipMoneyMaps?.currentIncomeSplit ?? null;
    const { deleteMoneyMapAsync } = useDeleteMoneyMapAndRefetch(membership);
    const { setMoneyMapToInactiveAsync } = useArchiveMoneyMap();
    const { mutateAsync: duplicateMoneyMap } = useDuplicateMoneyMap();
    const { createDraft, isPending: isCreatingMoneyMapperDraft } =
        useCreateMoneyMapperDraft(membership);
    const { updateTitle } = useUpdateMoneyMapTitle(membership?.id);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onDelete = async (id: string) => {
        setIsSubmitting(true);

        try {
            await deleteMoneyMapAsync(id);
            toast.show('Money map deleted!', MutationSuccessToastOptions);
        } catch (error) {
            captureException(error);
            toast.show('Error deleting money map', MutationErrorToastOptions());
        } finally {
            await refetchMoneyMaps();
            setIsSubmitting(false);
        }
    };

    const onArchive = async (id: string) => {
        setIsSubmitting(true);

        try {
            await setMoneyMapToInactiveAsync(id);
            toast.show('Money map archived!', MutationSuccessToastOptions);
        } catch (error) {
            captureException(error);
            toast.show(
                'Error archiving money map',
                MutationErrorToastOptions(),
            );
        } finally {
            await refetchMoneyMaps();
            setIsSubmitting(false);
        }
    };

    const onDuplicate = async (id: string) => {
        setIsSubmitting(true);

        try {
            await duplicateMoneyMap({
                urlParams: {
                    id,
                },
            });
        } catch (error) {
            captureException(error);
            toast.show(
                'Error duplicating money map',
                MutationErrorToastOptions(),
            );
        } finally {
            await refetchMoneyMaps();
            setIsSubmitting(false);
        }
    };

    const onCreateMoneyMapperDraft = async () => {
        if (membership?.id == null) {
            return;
        }

        setIsSubmitting(true);

        try {
            const created = await createDraft();

            if (created?.id != null) {
                window.open(
                    `/money-mapper/${membership.id}?moneyMapId=${created.id}`,
                    '_blank',
                );
            }
        } catch (error) {
            captureException(error);
            toast.show(
                'Error creating money map draft',
                MutationErrorToastOptions(error),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const onRowEditComplete = async ({
        newData,
    }: DataTableRowEditCompleteEvent) => {
        const updatedMoneyMap = newData as MoneyMap;

        setIsSubmitting(true);

        try {
            await updateTitle(updatedMoneyMap, updatedMoneyMap.title);
            toast.show('Money map title updated!', MutationSuccessToastOptions);
        } catch (error) {
            captureException(error as Error);
            toast.show(
                'Error updating money map title',
                MutationErrorToastOptions((error as Error).message),
            );
        } finally {
            await refetchMoneyMaps();
            setIsSubmitting(false);
        }
    };

    const NoMoneyMaps = () => {
        return (
            <BodyMediumRegular fontStyle="italic">
                No Money Maps
            </BodyMediumRegular>
        );
    };

    const ActionButtons = () => {
        return (
            <View marginBottom={20} style={{ alignSelf: 'flex-end' }}>
                <XStack gap={12}>
                    <Button
                        variant="secondary"
                        size="small"
                        target="_blank"
                        href={{
                            pathname: '/retirement-calculator/[membershipId]',
                            params: { membershipId: membership?.id ?? '' },
                        }}
                    >
                        Retirement Calculator
                    </Button>
                    <Button
                        variant="secondary"
                        size="small"
                        onPress={() => {
                            void onCreateMoneyMapperDraft();
                        }}
                        disabled={isSubmitting || isCreatingMoneyMapperDraft}
                    >
                        Money Mapper (new)
                    </Button>
                    <Button
                        variant="primary"
                        size="small"
                        target="_blank"
                        href={{
                            pathname: '/money-map/[membershipId]',
                            params: { membershipId: membership?.id ?? '' },
                        }}
                    >
                        Create new Money Map
                    </Button>
                </XStack>
            </View>
        );
    };

    const MoneyMapDataTable = () => {
        return (
            <DataTable
                value={moneyMaps}
                size="small"
                tableStyle={{ width: '100%' }}
                scrollable
                scrollHeight="70vh"
                sortField="updatedAt"
                sortOrder={-1}
                editMode="row"
                onRowEditComplete={onRowEditComplete}
            >
                <Column field="id" header="id" hidden={true} />
                <Column
                    field="title"
                    header="Title"
                    editor={(options) => TextEditor({ options })}
                />
                <Column
                    field="updatedAt"
                    header="Last Edit"
                    sortable
                    body={({ updatedAt }: MoneyMap) => {
                        return formatToSimpleDateTimeString(updatedAt, {
                            second: '2-digit',
                        });
                    }}
                />
                <Column
                    field="status"
                    header="Status"
                    body={MoneyMapStatusRenderer}
                />
                <Column
                    field="isCurrentIncomeSplitSource"
                    header="Income split"
                    body={(moneyMap: MoneyMapWithGroveSplitContext) => (
                        <IncomeSplitSourceRenderer
                            moneyMap={moneyMap}
                            currentIncomeSplit={currentIncomeSplit}
                        />
                    )}
                />
                <Column
                    field="eligibleForMemberEdit"
                    header="Form"
                    style={{ width: '3rem', paddingLeft: 0 }}
                    body={MemberEditEligibleRenderer}
                />
                <Column
                    header="Edit"
                    style={{ width: '3rem', paddingLeft: 0 }}
                    body={(moneyMap: MoneyMap) => {
                        return canEditMoneyMap(moneyMap.status) ? (
                            <Link
                                target="_blank"
                                href={{
                                    pathname: '/money-map/[membershipId]',
                                    params: {
                                        membershipId: membership?.id ?? '',
                                        moneyMapId: moneyMap.id,
                                    },
                                }}
                            >
                                <EditItemRenderer onPress={() => {}} />
                            </Link>
                        ) : null;
                    }}
                    hidden={!canMutateMoneyMaps}
                />
                <Column
                    header="Mapper"
                    style={{ width: '3rem', paddingLeft: 0 }}
                    body={(moneyMap: MoneyMap) =>
                        canEditMoneyMap(moneyMap.status) ? (
                            <Link
                                target="_blank"
                                href={{
                                    pathname: '/money-mapper/[membershipId]',
                                    params: {
                                        membershipId: membership?.id ?? '',
                                        moneyMapId: moneyMap.id,
                                    },
                                }}
                            >
                                <YStack
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <IconButton
                                        variant="tertiary"
                                        icon={MoneyMapIcon}
                                        onPress={() => {}}
                                    />
                                </YStack>
                            </Link>
                        ) : null
                    }
                    hidden={!canMutateMoneyMaps}
                />
                <Column
                    header="Archive"
                    style={{ width: '3rem', paddingLeft: 0 }}
                    body={({ id, status }: MoneyMap) => {
                        return canArchiveMoneyMap(status) ? (
                            <ArchiveItemRenderer
                                onArchive={async () => {
                                    await onArchive(id);
                                }}
                            />
                        ) : null;
                    }}
                    hidden={!canMutateMoneyMaps}
                />
                <Column
                    header="Delete"
                    style={{ width: '3rem', paddingLeft: 0 }}
                    body={({ id, status }: MoneyMap) => {
                        return canDeleteMoneyMap(status) ? (
                            <DeleteItemRenderer
                                onDelete={async () => {
                                    await onDelete(id);
                                }}
                            />
                        ) : null;
                    }}
                    hidden={!canMutateMoneyMaps}
                />
                <Column
                    header="Preview"
                    style={{ width: '3rem', paddingLeft: 0 }}
                    body={({ id, status }: MoneyMap) => {
                        return canViewMoneyMap(status) ? (
                            <Link
                                target="_blank"
                                href={{
                                    pathname:
                                        '/money-map/[membershipId]/preview/[moneyMapId]',
                                    params: {
                                        membershipId: membership?.id ?? '',
                                        moneyMapId: id,
                                    },
                                }}
                            >
                                <ViewItemRenderer onPress={() => {}} />
                            </Link>
                        ) : null;
                    }}
                    hidden={!canMutateMoneyMaps}
                />
                <Column
                    header="Duplicate"
                    style={{ width: '3rem', paddingLeft: 0 }}
                    body={({ id }: MoneyMap) => {
                        return (
                            <DuplicateItemRenderer
                                onPress={async () => {
                                    await onDuplicate(id);
                                }}
                            />
                        );
                    }}
                    hidden={!canMutateMoneyMaps}
                />
                <Column rowEditor hidden={!canMutateMoneyMaps} />
            </DataTable>
        );
    };

    const showSpinner = isLoadingMoneyMaps || isSubmitting;

    if (errorLoadingMoneyMaps) {
        return <TextError message="Error loading money maps." />;
    }

    return (
        <YStack gap={12}>
            {showSpinner ? (
                <Spinner />
            ) : (moneyMaps ?? []).length === 0 ? (
                <YStack>
                    <NoMoneyMaps />
                    <ActionButtons />
                </YStack>
            ) : (
                <YStack>
                    <ActionButtons />
                    <MoneyMapDataTable />
                </YStack>
            )}
        </YStack>
    );
};

const MemberEditEligibleRenderer = ({
    eligibleForMemberEdit,
    advisoryMode,
}: MoneyMap) => {
    const formMarker =
        advisoryMode === MoneyMapAdvisoryMode.ROBO_ADVISOR_NO_IM
            ? { icon: BotIcon, tooltip: 'CMM finalized (no IM)' }
            : advisoryMode === MoneyMapAdvisoryMode.GUIDANCE_APPROVED
              ? { icon: BadgeCheckIcon, tooltip: 'Guide created' }
              : eligibleForMemberEdit === true
                ? { icon: UserCheckIcon, tooltip: 'Member edit' }
                : null;

    if (formMarker == null) {
        return null;
    }

    return (
        <YStack justifyContent="center" alignItems="center">
            <TooltipTrigger tooltipContent={formMarker.tooltip}>
                <Icon icon={formMarker.icon} size={20} color="$black" />
            </TooltipTrigger>
        </YStack>
    );
};

const IncomeSplitSourceRenderer = ({
    moneyMap,
    currentIncomeSplit,
}: {
    moneyMap: MoneyMapWithGroveSplitContext;
    currentIncomeSplit: CurrentIncomeSplitSnapshot | null;
}) => {
    if (!moneyMap.isCurrentIncomeSplitSource || currentIncomeSplit == null) {
        return null;
    }

    const statusLabel =
        currentIncomeSplit.status === 'ACTIVE'
            ? 'ACTIVE'
            : currentIncomeSplit.status === 'DELETED'
              ? 'DELETED'
              : 'INACTIVE';
    const statusSeverity: BadgeSeverity =
        currentIncomeSplit.status === 'ACTIVE'
            ? 'success'
            : currentIncomeSplit.status === 'DELETED'
              ? 'danger'
              : 'warning';
    const createdViaLabel = getIncomeSplitCreatedViaLabel(
        currentIncomeSplit.createdVia,
    );
    const createdViaSeverity: BadgeSeverity =
        currentIncomeSplit.createdVia ===
        IncomeSplitConfigCreatedVia.INCOME_SPLIT_EDITOR
            ? 'warning'
            : 'info';

    return (
        <YStack gap={4} alignItems="flex-start">
            <Badge severity={statusSeverity} value={statusLabel} />
            {createdViaLabel != null ? (
                <Badge severity={createdViaSeverity} value={createdViaLabel} />
            ) : null}
        </YStack>
    );
};

const MoneyMapStatusRenderer = ({ status }: MoneyMap) => {
    let badgeServerity: BadgeSeverity | null = null;
    if (status === MoneyMapStatus.ACTIVE) {
        badgeServerity = 'success';
    } else if (status === MoneyMapStatus.PREVIEW) {
        badgeServerity = 'info';
    } else if (status === MoneyMapStatus.DRAFT) {
        badgeServerity = 'warning';
    } else if (status === MoneyMapStatus.INACTIVE) {
        badgeServerity = 'danger';
    }

    return (
        <Badge
            severity={badgeServerity}
            value={MoneyMapGuideReadableStatusMap[status]}
        />
    );
};

interface ArchiveItemRendererProps {
    onArchive: () => void;
    confirmDialogProps?: ConfirmDialogProps;
}

const ArchiveItemRenderer = ({
    onArchive,
    confirmDialogProps,
}: ArchiveItemRendererProps) => {
    return (
        <YStack justifyContent="center" alignItems="center">
            <IconButton
                variant="tertiary"
                icon={ArchiveIcon}
                onPress={() => {
                    confirmDialog({
                        header: 'Are you sure?',
                        acceptLabel: 'Yes',
                        rejectLabel: 'Cancel',
                        accept: () => {
                            onArchive();
                        },
                        visible: true,
                        ...confirmDialogProps,
                    });
                }}
            />
        </YStack>
    );
};

const canEditMoneyMap = (status: MoneyMapStatus) => {
    return status === MoneyMapStatus.DRAFT || status === MoneyMapStatus.PREVIEW;
};

const canArchiveMoneyMap = (status: MoneyMapStatus) => {
    return status === MoneyMapStatus.ACTIVE;
};

const canDeleteMoneyMap = (status: MoneyMapStatus) => {
    return (
        status === MoneyMapStatus.DRAFT || status === MoneyMapStatus.INACTIVE
    );
};

const canViewMoneyMap = (status: MoneyMapStatus) => {
    return (
        status === MoneyMapStatus.ACTIVE || status === MoneyMapStatus.INACTIVE
    );
};
